import { use, useMemo, useState, useCallback, useEffect } from 'react';
import { usePath } from 'crossroad';
import PassageDetails from '../passage/passage-details';
import { CanonContextView } from '@/lib/canon-context/canon-context-view';
import { OutlineDataContext } from '@/data/outline/outline-data-context';
import { PassageDataContext } from '@/data/passage/passage-data-context';
import type { BibleBook } from '@/lib/utils/bible-utils';
import { parseRef } from '@/data/passage/passage-data-service';
import { usePassageState } from '@/lib/hooks/use-passage-state';
import { saveScrollPosition } from '@/lib/utils/scroll-position';
import {
    findOutlineContext,
    findNodeById,
    findChildContainingPassage,
    isLeafNode,
    createVirtualRootNode,
    findFirstSectionInOutline
} from '@/lib/outline/outline-tree-utils';
import { ContextLoadingState } from './ContextLoadingState';
import { ContextErrorState } from './ContextErrorState';

interface Props {
    passageId: string; // The initial passage ID (stays constant in the route)
}

export function CanonContext({passageId}: Props) {
    const { data: outlineData } = use(OutlineDataContext);
    const { data: passageData } = use(PassageDataContext);
    const { setScrollState } = usePassageState();
    const [, setPath] = usePath();

    // Find the outline and node containing the passage
    const outlineContext = useMemo(() => {
        if (!outlineData?.items) return undefined;
        return findOutlineContext(Object.values(outlineData.items), passageId);
    }, [passageId, outlineData?.items]);

    // Compute the default node ID (parent of passage node, or passage node itself if no parent)
    const defaultNodeId = useMemo(() => {
        if (!outlineContext) return undefined;
        return outlineContext.parentNode?.id || outlineContext.initialNode.id;
    }, [outlineContext]);

    // Track when user has navigated away from the default node (null means use default)
    const [overrideNodeId, setOverrideNodeId] = useState<string | null>(null);

    // Reset override when passage ID changes
    useEffect(() => {
        setOverrideNodeId(null);
    }, [passageId]);

    // The actual current node ID: override if set, otherwise default
    const currentNodeId = overrideNodeId ?? defaultNodeId;

    // Get the current node to display and its parent
    const currentNodeAndParent = useMemo(() => {
        if (!outlineContext || !currentNodeId) return undefined;

        // Special case: if currentNodeId is the outline book ID, we're showing the root level
        if (currentNodeId === outlineContext.outline.id) {
            return {
                node: createVirtualRootNode(outlineContext.outline),
                parent: undefined // No parent above the book level
            };
        }

        // Find the node by ID
        return findNodeById(outlineContext.outline.structure, currentNodeId);
    }, [currentNodeId, outlineContext]);

    // Helper: Update scroll state and localStorage before navigating to a passage
    const updateScrollStateForPassage = useCallback((sectionId: string) => {
        const passage = passageData?.items?.find(p => p.id === sectionId);
        const ref = parseRef(passage?.ref);
        if (!ref) return;

        const book = passage.book as BibleBook;
        const { chapterStart: chapter, verseStart: verse } = ref;

        // Update PassageState (for header display)
        setScrollState({book, chapter, verse});

        // Update localStorage (so /app redirect uses the latest position)
        saveScrollPosition(book, chapter, verse);
    }, [passageData?.items, setScrollState]);

    // Navigate to a node - either drill down (non-leaf) or go to passage view (leaf)
    const navigateToNode = useCallback((nodeId: string) => {
        if (!outlineContext) return;
        
        const nodeResult = findNodeById(outlineContext.outline.structure, nodeId);
        if (nodeResult && isLeafNode(nodeResult.node)) {
            const sectionId = nodeResult.node.sectionIds?.[0];
            if (sectionId) {
                updateScrollStateForPassage(sectionId);
                setPath(`/app/passage/${sectionId}`);
                return;
            }
        }
        
        // Non-leaf node or no section ID: drill down
        setOverrideNodeId(nodeId);
    }, [outlineContext, setPath, updateScrollStateForPassage]);

    // Navigate to context view (for rail clicks on leaf nodes)
    const navigateToNodeContext = useCallback((nodeId: string) => {
        if (!outlineContext) return;
        
        const nodeResult = findNodeById(outlineContext.outline.structure, nodeId);
        if (nodeResult && isLeafNode(nodeResult.node)) {
            const passageId = nodeResult.node.sectionIds?.[0];
            if (passageId) {
                setPath(`/app/passage/${passageId}/context/canon`);
                return;
            }
        }
        
        // Non-leaf node: drill down
        setOverrideNodeId(nodeId);
    }, [outlineContext, setPath]);

    // Navigate back in (zoom back in one level toward the highlighted passage)
    const navigateBackIn = useCallback(() => {
        if (!outlineContext || !currentNodeAndParent?.node) return;

        const childWithPassage = findChildContainingPassage(currentNodeAndParent.node, passageId);

        if (childWithPassage) {
            // Check if the child is a leaf node (contains the passage directly)
            if (isLeafNode(childWithPassage)) {
                // Leaf node - go to passage details
                updateScrollStateForPassage(passageId);
                setPath(`/app/passage/${passageId}`);
            } else {
                // Non-leaf node - drill down to it
                setOverrideNodeId(childWithPassage.id);
            }
        } else {
            // No child found, go to passage details
            updateScrollStateForPassage(passageId);
            setPath(`/app/passage/${passageId}`);
        }
    }, [outlineContext, currentNodeAndParent, passageId, setPath, updateScrollStateForPassage]);

    // Navigate back to parent
    const navigateToParent = useCallback(() => {
        if (!outlineContext || !currentNodeAndParent?.node) return;

        if (currentNodeAndParent.parent) {
            // Navigate to the parent node
            setOverrideNodeId(currentNodeAndParent.parent.id);
        } else if (currentNodeId === outlineContext.outline.id) {
            // Already at the book level (showing all root nodes), go to passage details
            updateScrollStateForPassage(passageId);
            setPath(`/app/passage/${passageId}`);
        } else {
            // No parent - we're at a root-level node, zoom out to show all root nodes
            setOverrideNodeId(outlineContext.outline.id);
        }
    }, [outlineContext, currentNodeAndParent, passageId, setPath, currentNodeId, updateScrollStateForPassage]);

    // Can zoom out if we have a parent, or if we're at root level but not yet at book level
    const canZoomOut = useMemo(() => {
        if (!outlineContext || !currentNodeAndParent) return false;
        return currentNodeAndParent.parent !== undefined || currentNodeId !== outlineContext.outline.id;
    }, [outlineContext, currentNodeAndParent, currentNodeId]);


    // Navigation handler for CanonRail book selection
    const handleBookSelect = useCallback((book: BibleBook) => {
        if (!outlineData?.items) return;
        
        const bookOutline = Object.values(outlineData.items).find(o => o.book === book);
        if (!bookOutline) return;
        
        const firstPassageId = findFirstSectionInOutline(bookOutline);
        if (firstPassageId) {
            setPath(`/app/passage/${firstPassageId}/context/canon`);
        }
    }, [outlineData?.items, setPath]);

    // Loading state
    if (!outlineData?.items) {
        return <ContextLoadingState />;
    }

    // Error state
    if (!outlineContext) {
        return <ContextErrorState onReturn={() => setPath('/app')} />;
    }

    return (
        <CanonContextView
            outline={outlineContext.outline}
            currentNode={currentNodeAndParent?.node}
            highlightedPassageId={passageId}
            canZoomOut={canZoomOut}
            onNavigateToNode={navigateToNode}
            onNavigateToNodeContext={navigateToNodeContext}
            onNavigateToParent={navigateToParent}
            onNavigateBackIn={navigateBackIn}
            onBookSelect={handleBookSelect}
        >
            <PassageDetails passageId={passageId} />
        </CanonContextView>
    );
}
